// Thin fetch layer over Api/LearnController. A 401 becomes SignInRequired so the UI can send the
// reader to the login page with a returnUrl instead of failing quietly.

export class SignInRequired extends Error {
  constructor() {
    super('sign in required');
  }
}

export interface Annotation {
  id: number;
  slug: string;
  kind: number; // 0 highlight, 1 note, 2 comment
  quote: string | null;
  prefix: string | null;
  suffix: string | null;
  body: string | null;
  isPublic: boolean;
  mine: boolean;
  author: string;
  createdAt: string;
}

export interface TutorMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

export interface QuizBest {
  score: number;
  total: number;
}

export interface LearnState {
  signedIn: boolean;
  webmaster: boolean;
  read: string[];
  quiz: Record<string, QuizBest>;
  annotations: Annotation[];
  comments: Annotation[];
  tutor: TutorMessage[];
}

export interface QuizResult {
  score: number;
  total: number;
  saved: boolean;
  results: { correct: boolean; answer: number; explanation: string }[];
}

async function call<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'same-origin',
  });
  if (res.status === 401) {
    throw new SignInRequired();
  }
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const j = await res.json();
      if (j && j.error) message = j.error;
      else if (j && j.Error) message = j.Error;
    } catch {
      /* not json */
    }
    throw new Error(message);
  }
  const text = await res.text();
  return (text ? camelize(JSON.parse(text)) : undefined) as T;
}

// The API serializes C# names as they are (PascalCase). Lower-case the first letter of every key,
// recursively, so the types above can stay idiomatic. Slugs used as dictionary keys are already
// lower-case, so they pass through unchanged.
function camelize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(camelize);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k.charAt(0).toLowerCase() + k.slice(1)] = camelize(v);
    }
    return out;
  }
  return value;
}

export const LearnApi = {
  state: (slug: string) => call<LearnState>('GET', `/api/learn/state?slug=${encodeURIComponent(slug)}`),
  progress: (slug: string, read: boolean) =>
    call<{ read: string[] }>('POST', '/api/learn/progress', { slug, read }),
  createAnnotation: (a: {
    slug: string;
    kind: number;
    quote?: string | null;
    prefix?: string | null;
    suffix?: string | null;
    body?: string | null;
  }) => call<Annotation>('POST', '/api/learn/annotations', a),
  updateAnnotation: (id: number, body: string) =>
    call<void>('PUT', `/api/learn/annotations/${id}`, { body }),
  deleteAnnotation: (id: number) => call<void>('DELETE', `/api/learn/annotations/${id}`),
  quiz: (slug: string, answers: number[]) =>
    call<QuizResult>('POST', '/api/learn/quiz', { slug, answers }),
  tutor: (slug: string, message: string, quote?: string | null) =>
    call<{ id: number; reply: string }>('POST', '/api/learn/tutor', { slug, message, quote }),
  clearTutor: (slug: string) => call<void>('DELETE', `/api/learn/tutor?slug=${encodeURIComponent(slug)}`),
};
